<template>
  <section class="app-table-wrap payments-table-wrap">
    <div class="app-table-scroll">
      <table class="app-data-table">
        <thead>
          <tr>
            <th class="app-col-index">#</th>
            <th>
              <button
                type="button"
                class="payments-table-sort-btn"
                :title="text.table.sortTitles.paymentDate"
                @click="toggleSort('date')"
              >
                <span>{{ text.table.columns.paymentDate }}</span>
                <i class="fa" :class="sortIconClass('date')" aria-hidden="true"></i>
              </button>
            </th>
            <th v-if="showCustomerColumn">
              <button
                type="button"
                class="payments-table-sort-btn"
                :title="text.table.sortTitles.customer"
                @click="toggleSort('customer')"
              >
                <span>{{ text.table.columns.customer }}</span>
                <i class="fa" :class="sortIconClass('customer')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ text.table.columns.concept }}</th>
            <th class="app-text-right">
              <button
                type="button"
                class="payments-table-sort-btn payments-table-sort-btn--right"
                :title="text.table.sortTitles.subtotal"
                @click="toggleSort('subtotal')"
              >
                <span>{{ text.table.columns.subtotal }}</span>
                <i class="fa" :class="sortIconClass('subtotal')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ text.table.columns.taxes }}</th>
            <th class="app-text-right">
              <button
                type="button"
                class="payments-table-sort-btn payments-table-sort-btn--right"
                :title="text.table.sortTitles.total"
                @click="toggleSort('total')"
              >
                <span>{{ text.table.columns.total }}</span>
                <i class="fa" :class="sortIconClass('total')" aria-hidden="true"></i>
              </button>
            </th>
            <th>{{ text.table.columns.invoice }}</th>
            <th>{{ text.table.columns.payment }}</th>
            <th>{{ text.table.columns.paymentStatus }}</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          <tr v-if="loading">
            <td :colspan="columnCount" class="app-table-empty">
              {{ text.table.loading }}
            </td>
          </tr>

          <tr v-else-if="sortedRows.length === 0">
            <td :colspan="columnCount" class="app-table-empty">
              {{ text.table.empty }}
            </td>
          </tr>

          <tr v-else v-for="(row, index) in sortedRows" :key="row.id">
            <td class="app-muted">{{ index + 1 }}</td>
            <td>{{ formatDate(row.date) }}</td>
            <td v-if="showCustomerColumn" class="app-strong">
              <a
                v-if="resolveCustomerId(row)"
                :href="resolveCustomerHref(row)"
                class="payments-table-link"
                :title="text.table.links.customerDetail"
                @click="navigateToCustomerDetail(row, $event)"
              >
                {{ resolveCustomerName(row) }}
              </a>
              <span v-else>{{ resolveCustomerName(row) }}</span>
            </td>
            <td>{{ row.concept }}</td>
            <td class="app-text-right app-strong">
              <strong class="amount-cell-subtotal">{{ formatAmountWithCurrency(resolveSubtotal(row), row.currency) }}</strong>
            </td>
            <td>
              <div class="payments-cell-stack" v-if="resolveTaxItems(row).length > 0">
                <small
                  v-for="(taxItem, taxIndex) in resolveTaxItems(row)"
                  :key="`${row.id}-tax-${taxIndex}`"
                  class="amount-cell-tax-line"
                >
                  {{ formatTaxLine(taxItem, row.currency) }}
                </small>
              </div>
              <span v-else></span>
            </td>
            <td class="app-text-right app-strong">
              <div class="amount-cell">
                <span class="amount-cell-total">{{ formatAmountWithCurrency(resolveTotal(row), row.currency) }}</span>
                <small v-if="isForeignCurrency(row.currency)" class="amount-cell-exchange-rate">
                  {{ text.table.exchangeRatePrefix }} {{ formatExchangeRate(resolveExchangeRate(row)) }}
                </small>
              </div>
            </td>
            <td>
              <button
                v-if="canIssueInvoice(row)"
                type="button"
                class="payments-action-link"
                @click="$emit('invoice-plan', row)"
              >
                {{ text.table.actions.issueInvoice }}
              </button>
              <button
                v-else-if="canIssueSimpleNote(row)"
                type="button"
                class="payments-action-link"
                @click="$emit('simple-note-plan', row)"
              >
                {{ text.table.actions.issueSimpleNote }}
              </button>
              <a
                v-else-if="canOpenInvoiceDetail(row)"
                :href="resolveInvoiceHref(row)"
                class="payments-table-link payment-billing-link"
                :title="text.table.links.invoiceDetail"
                @click="navigateToInvoiceDetail(row, $event)"
              >
                {{ resolveBillingReference(row) || text.table.links.invoiceDefaultLabel }}
              </a>
              <a
                v-else-if="canOpenSimpleNoteDetail(row)"
                :href="resolveSimpleNoteHref(row)"
                class="payments-table-link payment-billing-link"
                :title="text.table.links.simpleNoteDetail"
                @click="navigateToSimpleNoteDetail(row, $event)"
              >
                {{ resolveBillingReference(row) || text.table.links.invoiceDefaultLabel }}
              </a>
              <span v-else class="payment-billing-reference">{{ resolveBillingReference(row) }}</span>
            </td>
            <td>
              <button
                v-if="canShowPayButton(row)"
                type="button"
                class="payments-action-link"
                @click="$emit('pay-plan', row)"
              >
                {{ text.table.actions.pay }}
              </button>
              <a
                v-else-if="hasPaymentReceiptRoute(row)"
                :href="resolvePaymentHref(row)"
                class="payments-table-link payment-reference"
                :title="text.table.links.paymentDetail"
                @click="navigateToPaymentDetail(row, $event)"
              >
                {{ resolvePaymentReference(row) }}
              </a>
              <span v-else class="payment-reference">{{ resolvePaymentReference(row) }}</span>
            </td>
            <td>
              <span class="app-status-pill" :class="statusClass(row.payment_status_code)">
                {{ statusLabel(row.payment_status_code) }}
              </span>
            </td>
            <td class="app-table-actions-cell">
              <div class="app-table-actions-inline">
                <button
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--edit"
                  :title="text.table.actions.edit"
                  @click="$emit('edit-plan', row)"
                >
                  <i class="fa fa-pencil" aria-hidden="true"></i>
                </button>

                <button
                  v-if="canDeletePlan(row)"
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--delete"
                  :title="text.table.actions.delete"
                  @click="$emit('delete-plan', row)"
                >
                  <i class="fa fa-trash" aria-hidden="true"></i>
                </button>

                <button
                  v-else
                  type="button"
                  class="app-table-icon-btn app-table-icon-btn--clone"
                  :title="text.table.actions.clonePayment"
                  @click="$emit('duplicate-plan', row)"
                >
                  <i class="fa fa-clone" aria-hidden="true"></i>
                </button>

                <div class="app-table-actions-menu-wrap">
                  <button
                    type="button"
                    class="app-table-icon-btn"
                    :title="text.table.actions.more"
                    @click="toggleActionsMenu(row.id, $event)"
                  >
                    <i class="fa fa-ellipsis-h" aria-hidden="true"></i>
                  </button>
                </div>
              </div>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <teleport to="body">
      <div
        v-if="openRow"
        class="app-table-actions-menu app-table-floating-menu"
        :style="floatingMenuStyle"
      >
        <button
          v-if="canDeletePlan(openRow)"
          type="button"
          @click="emitAction('duplicate-plan', openRow)"
        >
          {{ text.table.actions.clonePayment }}
        </button>
        <button type="button" @click="emitAction('credit-note', openRow)">{{ text.table.actions.creditNote }}</button>
      </div>
    </teleport>
  </section>
</template>

<script>
import { formatDateShort } from '@/utils/datePresentation';
import { useDomainText } from '@/composables/text/useDomainText';
import {
  formatAmountWithCurrency,
  formatExchangeRate,
  getRequiredExchangeRate,
  isForeignCurrency,
} from '@/utils/numberFormat';
import { normalizePaymentPlanStatusCode } from '@/composables/paymentPlans/usePaymentPlanNormalization';
import { formatTaxLine } from '@/composables/shared/useTaxPresentation';
import {
  navigateToRouteDetail,
  resolveRouteHref,
} from '@/composables/shared/useNavigation';
import {
  computeFloatingMenuPosition,
  resolveToggledMenuId,
} from '@/composables/shared/useFloatingActionsMenu';
import {
  resolveSortChange,
  resolveSortIconClass,
  resolveSortTogglePayload,
  sortRows,
} from '@/composables/shared/useSorting';
import {
  canIssuePaymentInvoice,
  canIssuePaymentSimpleNote,
  canOpenPaymentInvoiceDetail,
  canShowPayActionButton,
  hasPaymentInvoiceDocument,
  hasSimpleNoteVoucherDocument,
  isSimpleNotePaymentVoucher,
  resolvePaymentBillingReference,
  resolvePaymentInvoiceId,
} from '@/composables/shared/usePaymentVoucherActions';

const { catalog: text } = useDomainText('payments');

export default {
  name: 'SalePaymentPlanTable',
  props: {
    rows: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    showCustomerColumn: {
      type: Boolean,
      default: false,
    },
    contextSaleId: {
      type: Number,
      default: 0,
    },
    contextSaleCode: {
      type: String,
      default: '',
    },
    contextSaleName: {
      type: String,
      default: '',
    },
  },
  emits: [
    'pay-plan',
    'edit-plan',
    'delete-plan',
    'invoice-plan',
    'simple-note-plan',
    'duplicate-plan',
    'credit-note',
  ],
  data() {
    return {
      text,
      openActionsMenuId: null,
      floatingMenuTop: 0,
      floatingMenuLeft: 0,
      tableSort: {
        field: 'date',
        direction: 'asc',
      },
    };
  },
  computed: {
    normalizedRows() {
      return Array.isArray(this.rows) ? this.rows : [];
    },
    sortedRows() {
      return sortRows(this.normalizedRows, this.tableSort, {
        date: (row) => new Date(String(row?.date || '')).getTime() || 0,
        customer: (row) => this.resolveCustomerName(row),
        subtotal: (row) => Number(this.resolveSubtotal(row) || 0),
        total: (row) => Number(this.resolveTotal(row) || 0),
      });
    },
    columnCount() {
      return this.showCustomerColumn ? 11 : 10;
    },
    openRow() {
      if (!this.openActionsMenuId) {
        return null;
      }

      return this.rows.find((row) => Number(row?.id || 0) === Number(this.openActionsMenuId)) || null;
    },
    floatingMenuStyle() {
      return {
        top: `${this.floatingMenuTop}px`,
        left: `${this.floatingMenuLeft}px`,
      };
    },
  },
  mounted() {
    document.addEventListener('click', this.handleDocumentClick);
  },
  beforeUnmount() {
    document.removeEventListener('click', this.handleDocumentClick);
  },
  methods: {
    formatAmountWithCurrency,
    formatExchangeRate,
    isForeignCurrency,
    formatTaxLine,
    sortIconClass(field) {
      return resolveSortIconClass(this.tableSort, field);
    },
    toggleSort(field) {
      const nextSortPayload = resolveSortTogglePayload(
        this.tableSort,
        field,
        ['date', 'customer', 'subtotal', 'total'],
      );

      if (!nextSortPayload) {
        return;
      }

      this.tableSort = resolveSortChange(
        this.tableSort,
        nextSortPayload,
        ['date', 'customer', 'subtotal', 'total'],
      );
    },
    formatDate(value) {
      return formatDateShort(value);
    },
    resolveCustomerId(row) {
      return Number(row?.company_id || row?.customer_id || 0);
    },
    resolveCustomerName(row) {
      return String(row?.customer_name || row?.company_name || '').trim() || '-';
    },
    resolveCustomerHref(row) {
      return resolveRouteHref(this.$router, 'customerDetail', 'customerId', this.resolveCustomerId(row));
    },
    navigateToCustomerDetail(row, event = null) {
      navigateToRouteDetail(
        this.$router,
        'customerDetail',
        'customerId',
        this.resolveCustomerId(row),
        event,
      );
    },
    resolveExchangeRate(row) {
      return getRequiredExchangeRate(row);
    },
    resolveSubtotal(row) {
      return Number(row?.subtotal || 0) || 0;
    },
    resolveTotal(row) {
      const explicitTotal = Number(row?.total || 0);
      if (Number.isFinite(explicitTotal) && explicitTotal > 0) {
        return explicitTotal;
      }

      const subtotal = this.resolveSubtotal(row);
      const transferred = Number(row?.taxes?.transferred_total || 0) || 0;
      const withheld = Number(row?.taxes?.withheld_total || 0) || 0;

      return subtotal + transferred - withheld;
    },
    resolveTaxItems(row) {
      const items = Array.isArray(row?.taxes?.items) ? row.taxes.items : [];
      if (items.length > 0) {
        return items;
      }

      const transferred = Number(row?.taxes?.transferred_total || 0) || 0;
      const withheld = Number(row?.taxes?.withheld_total || 0) || 0;
      const fallback = [];

      if (transferred > 0) {
        fallback.push({ name: this.text.table.taxes.transferred, direction: 'trasladado', amount: transferred });
      }

      if (withheld > 0) {
        fallback.push({ name: this.text.table.taxes.withheld, direction: 'retenido', amount: withheld });
      }

      return fallback;
    },
    statusLabel(statusCode) {
      return normalizePaymentPlanStatusCode(statusCode) === 'DONE'
        ? this.text.table.status.paid
        : this.text.table.status.pending;
    },
    statusClass(statusCode) {
      return normalizePaymentPlanStatusCode(statusCode) === 'DONE' ? 'app-status-active' : 'app-status-inactive';
    },
    isSimpleNoteVoucher(row) {
      return isSimpleNotePaymentVoucher(row);
    },
    hasInvoice(row) {
      return hasPaymentInvoiceDocument(row, {
        invoiceReferenceKeys: ['invoice'],
      });
    },
    resolveSimpleNoteId(row) {
      return Number(row?.simple_note_id || 0) || 0;
    },
    canOpenSimpleNoteDetail(row) {
      return this.resolveSimpleNoteId(row) > 0;
    },
    resolveSimpleNoteHref(row) {
      return resolveRouteHref(this.$router, 'simpleNoteDetail', 'simpleNoteId', this.resolveSimpleNoteId(row));
    },
    navigateToSimpleNoteDetail(row, event = null) {
      navigateToRouteDetail(
        this.$router,
        'simpleNoteDetail',
        'simpleNoteId',
        this.resolveSimpleNoteId(row),
        event,
      );
    },
    resolveInvoiceId(row) {
      return resolvePaymentInvoiceId(row);
    },
    canOpenInvoiceDetail(row) {
      return canOpenPaymentInvoiceDetail(row, {
        invoiceReferenceKeys: ['invoice'],
      });
    },
    resolveInvoiceHref(row) {
      return resolveRouteHref(this.$router, 'invoiceDetail', 'invoiceId', this.resolveInvoiceId(row));
    },
    navigateToInvoiceDetail(row, event = null) {
      if (!this.canOpenInvoiceDetail(row)) {
        return;
      }

      if (
        event
        && (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey)
      ) {
        return;
      }

      if (event) {
        event.preventDefault();
      }

      const invoiceId = this.resolveInvoiceId(row);
      const rowSaleId = Number(row?.sale_id || row?.saleId || 0) || 0;
      const saleId = rowSaleId > 0 ? rowSaleId : (Number(this.contextSaleId || 0) || 0);
      const saleCode = String(row?.sale_code || this.contextSaleCode || '').trim();
      const saleName = String(
        row?.sale_name
        || row?.business_name
        || this.contextSaleName
        || ''
      ).trim();

      try {
        sessionStorage.setItem('app.breadcrumb.invoice-detail', JSON.stringify({
          contextId: String(invoiceId),
          source: 'sale-detail',
          saleId,
          saleName,
          customerId: Number(row?.company_id || row?.customer_id || 0) || 0,
          customerName: String(row?.company_name || row?.customer_name || '').trim(),
        }));
      } catch (error) {
        // Keep navigation flow even if session storage is unavailable.
      }

      this.$router.push({
        name: 'invoiceDetail',
        params: { invoiceId: String(invoiceId) },
        state: {
          invoiceSnapshot: {
            id: invoiceId,
            sale_id: saleId,
            sale_code: saleCode,
            sale_name: saleName,
            customer_id: Number(row?.company_id || row?.customer_id || 0) || 0,
            customer_name: String(row?.company_name || row?.customer_name || '').trim(),
            due_date: String(row?.date || '').trim(),
            status_key: normalizePaymentPlanStatusCode(row?.payment_status_code) === 'DONE' ? 'done' : 'pending',
            status: this.statusLabel(row?.payment_status_code),
            payment_status: this.statusLabel(row?.payment_status_code),
            payment_id: Number(row?.payment_id || 0) || 0,
            payment_receipt_id: Number(row?.payment_receipt_id || 0) || 0,
            payment_receipt_code: String(row?.payment_receipt_code || '').trim(),
            payment_reference: String(row?.payment || row?.payment_reference || '').trim(),
            balance: this.isPaid(row) ? 0 : (Number(row?.total || 0) || 0),
            has_received_proof: false,
            last_sent_at: '',
          },
        },
      }).catch(() => {});
    },
    hasSimpleNote(row) {
      return hasSimpleNoteVoucherDocument(row);
    },
    hasPayment(row) {
      return String(row?.payment || '').trim() !== '';
    },
    resolvePaymentReceiptId(row) {
      return Number(row?.payment_receipt_id || row?.payment_receipt?.id || 0) || 0;
    },
    hasPaymentReceiptRoute(row) {
      return this.resolvePaymentReceiptId(row) > 0;
    },
    resolvePaymentHref(row) {
      return resolveRouteHref(
        this.$router,
        'paymentReceiptDetail',
        'paymentReceiptId',
        this.resolvePaymentReceiptId(row),
      );
    },
    navigateToPaymentDetail(row, event = null) {
      navigateToRouteDetail(
        this.$router,
        'paymentReceiptDetail',
        'paymentReceiptId',
        this.resolvePaymentReceiptId(row),
        event,
      );
    },
    resolvePaymentReference(row) {
      return String(row?.payment || row?.payment_receipt_code || '').trim();
    },
    canIssueInvoice(row) {
      return canIssuePaymentInvoice(row, {
        isPaid: this.isPaid(row),
        invoiceReferenceKeys: ['invoice'],
      });
    },
    canIssueSimpleNote(row) {
      return canIssuePaymentSimpleNote(row);
    },
    resolveBillingReference(row) {
      return resolvePaymentBillingReference(row, {
        invoiceReferenceKeys: ['invoice'],
      });
    },
    canShowPayButton(row) {
      return canShowPayActionButton(row, {
        hasPayment: this.hasPayment(row),
        invoiceReferenceKeys: ['invoice'],
      });
    },
    isPaid(row) {
      return normalizePaymentPlanStatusCode(row?.payment_status_code) === 'DONE';
    },
    canDeletePlan(row) {
      return !this.hasInvoice(row)
        && !this.hasSimpleNote(row)
        && !this.hasPayment(row)
        && !this.isPaid(row);
    },
    toggleActionsMenu(rowId, event) {
      const normalizedId = Number(rowId || 0);
      if (!normalizedId) {
        return;
      }

      event?.stopPropagation();

      this.openActionsMenuId = resolveToggledMenuId(this.openActionsMenuId, normalizedId);
      this.updateFloatingMenuPosition(event?.currentTarget || null);
    },
    updateFloatingMenuPosition(triggerElement) {
      if (!triggerElement || this.openActionsMenuId === null) {
        return;
      }

      const position = computeFloatingMenuPosition(triggerElement, 220, 8);
      this.floatingMenuTop = position.top;
      this.floatingMenuLeft = position.left;
    },
    handleDocumentClick(event) {
      const target = event?.target;
      if (!target) {
        return;
      }

      if (target.closest('.app-table-actions-menu') || target.closest('.app-table-icon-btn')) {
        return;
      }

      this.openActionsMenuId = null;
    },
    emitAction(actionName, row) {
      this.openActionsMenuId = null;
      this.$emit(actionName, row);
    },
  },
};
</script>

<style scoped>
.payments-table-wrap {
  margin-top: 0;
}

.payments-table-sort-btn {
  width: 100%;
  border: 0;
  background: transparent;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 0;
  font-size: inherit;
  font-weight: inherit;
  color: inherit;
  cursor: pointer;
}

.payments-table-sort-btn--right {
  justify-content: flex-end;
}

.payments-cell-stack {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.payments-action-link {
  border: 0;
  background: transparent;
  padding: 0;
  font-size: 12px;
  font-weight: 700;
  color: #0070cd;
  text-decoration: none;
  cursor: pointer;
}

.payments-action-link:hover {
  color: #005a9f;
}

.payments-table-link {
  color: #0070cd;
  text-decoration: none;
  cursor: pointer;
  font-weight: 700;
}

.payment-billing-link {
  font-weight: 400;
}

.payments-table-link:hover {
  text-decoration: underline;
}

.payment-billing-reference {
  display: block;
  text-align: left;
}

.payment-reference {
  display: block;
  text-align: left;
}

.amount-cell {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
}

.amount-cell-subtotal {
  font-size: 12px;
  font-weight: 700;
  line-height: 1.2;
  color: #111827;
}

.amount-cell-tax-line {
  font-size: 11px;
  font-weight: 500;
  line-height: 1.2;
  color: #64748b;
}

.amount-cell-total {
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  color: #0b5394;
}

.amount-cell-exchange-rate {
  font-size: 10px;
  line-height: 1;
  font-weight: 600;
  color: #6b7280;
}

.app-table-icon-btn--clone {
  color: #0369a1;
}
</style>
